import React from 'react';

import { DeviceContext } from '@/components/DeviceProvider/DeviceProvider';
import { GetGameLaunchStrategy } from '@/components/LaunchStrategiesProvider/utils';
import { UserSessionContext } from '@/components/UserSessionProvider';
import { mapUrl } from '@/logic/main/utils';
import { CategoryFinancialWebAttributes } from '@/types/category/categoryTypes';
import { LaunchStrategyEnum } from '@/types/gameTypes';
import { isHybrid888 } from '@/utils/isHybrid888/isHybrid888';

import styles from './GameLink.module.scss';
import { openGameInHybrid888App } from './openGameInHybrid888App';
import { openInNewWindow } from './openInNewWindow';

interface GameLinkProps {
  target?: string;
  url: string;
  shouldMapUrl?: boolean;
  categoryAttributes?: CategoryFinancialWebAttributes;
  notIndexed?: boolean;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  className?: string;
  integration?: string;
  dataTestId?: string;
  children?: React.ReactNode;
  emptySpaceChar?: boolean;
}

const GameLink = ({
  target = '_blank',
  url,
  shouldMapUrl = true,
  onClick = () => {},
  integration = '',
  className = styles.anchor,
  children,
  dataTestId = 'game-link',
  notIndexed,
  emptySpaceChar = true,
  categoryAttributes,
}: GameLinkProps) => {
  const { deviceType, deviceOs } = React.useContext(DeviceContext);
  const { loggedIn } = React.useContext(UserSessionContext);
  const launchStrategy = GetGameLaunchStrategy(integration);

  const shouldLaunchInPopup =
    launchStrategy === LaunchStrategyEnum['newWindow'];

  // Check if we are in Hybrid888App
  const isHybrid888App = isHybrid888();

  const linkUrl = shouldMapUrl
    ? mapUrl(url, deviceType, deviceOs, categoryAttributes)
    : url;

  const openerReferer = target === '_blank' ? 'noopener noreferrer ' : '';
  const noFollow = notIndexed ? 'nofollow' : '';

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // login popup is shown to authorize user and after game is launched
    if (!loggedIn) return onClick(event);

    // Hybrid888 game launch strategy, expected user to be logged in.
    if (isHybrid888App) {
      event.preventDefault();
      openGameInHybrid888App(linkUrl);
      return onClick(event);
    }

    if (!shouldLaunchInPopup) return onClick(event);

    event.preventDefault();
    openInNewWindow(linkUrl);
    onClick(event);
  };

  return (
    <a
      className={className}
      data-testid={dataTestId}
      target={target}
      rel={`${openerReferer}${noFollow}`}
      href={linkUrl}
      onClick={event => handleClick(event)}
    >
      {emptySpaceChar && <span>&nbsp;</span>}
      {children}
    </a>
  );
};

export default GameLink;
